/**
 * TenancyAmendments — Vertragsänderungen mit Wirkungsdatum (P2).
 *
 * Zeigt eine chronologische Liste aller Änderungen (Mietanpassung, VZ-Änderung,
 * Heizkosten, Stellplatz, Einheit +/-) und bietet einen einfachen Add-Dialog.
 * Beim Speichern werden die zugrundeliegenden rent_rules automatisch angepasst.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, TrendUp, TrendDown, House, NotePencil, Warning, Download } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";

const TYPE_META = {
  rent_change:      { label: "Mietanpassung", icon: TrendUp, color: "text-blue-700 bg-blue-50" },
  utilities_change: { label: "NK-Vorauszahlung ändern", icon: TrendDown, color: "text-emerald-700 bg-emerald-50" },
  heating_change:   { label: "Heizkosten ändern", icon: TrendDown, color: "text-rose-700 bg-rose-50" },
  parking_change:   { label: "Stellplatz-Miete ändern", icon: TrendUp, color: "text-purple-700 bg-purple-50" },
  unit_added:       { label: "Einheit hinzugefügt", icon: House, color: "text-emerald-700 bg-emerald-50" },
  unit_removed:     { label: "Einheit entfernt", icon: House, color: "text-slate-600 bg-slate-100" },
  other:            { label: "Sonstige", icon: NotePencil, color: "text-slate-600 bg-slate-100" },
};
const eur = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

export default function TenancyAmendments({ tenancyId, onChanged }) {
  const [items, setItems] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    amendment_type: "rent_change",
    effective_date: "",
    new_value: "",
    reason: "",
    auto_regenerate: true,
  });
  const [undoPreview, setUndoPreview] = useState(null); // { amendment_id, months, category, effective_date }
  const [undoLoading, setUndoLoading] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, docsRes] = await Promise.all([
        api.get(`/tenancies/${tenancyId}/amendments`),
        api.get("/documents", {
          params: { tenancy_id: tenancyId, categories: "Vertragsänderung,amendment" },
        }).catch(() => ({ data: [] })),
      ]);
      setItems(data.amendments || []);
      setUploadedDocs(docsRes.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (tenancyId) load(); /* eslint-disable-next-line */ }, [tenancyId]);

  const create = async () => {
    if (!form.effective_date) { toast.error("Wirkungsdatum wird benötigt"); return; }
    if (form.new_value === "" && !["unit_added", "unit_removed", "other"].includes(form.amendment_type)) {
      toast.error("Neuer Wert wird benötigt");
      return;
    }
    try {
      // iter159g — Zuerst normal speichern (Backend regeneriert Sollstellungen)
      // iter159i — 0 muss auch als Wert akzeptiert werden (nicht als „leer" gewertet).
      const numVal = form.new_value === "" || form.new_value === null || form.new_value === undefined
        ? null
        : Number(form.new_value);
      const { data } = await api.post(`/tenancies/${tenancyId}/amendments`, {
        amendment_type: form.amendment_type,
        effective_date: form.effective_date,
        new_value: numVal,
        reason: form.reason || undefined,
        auto_regenerate: form.auto_regenerate,
      });
      const created = data?.created || 0;
      const deleted = data?.deleted || 0;
      toast.success(`Änderung gespeichert · ${deleted} Sollstellung(en) ersetzt, ${created} neu angelegt.`);
      // iter159g — Danach prüfen, ob Vor-Verwalter-Ausgleich-Ist-Buchungen für den
      // geänderten Bereich existieren, und den User fragen, ob sie entfernt
      // werden sollen (Ratio: alte Ausgleiche „decken" alte Sollbeträge —
      // nach einer Anpassung müssen sie auf null gesetzt und ggf. neu angelegt
      // werden, damit das Mietkonto konsistent bleibt).
      const cat = ({ rent_change: "rent_cold", utilities_change: "utilities", heating_change: "heating", parking_change: "parking" })[form.amendment_type];
      if (cat) {
        try {
          const { data: check } = await api.get(
            `/tenancies/${tenancyId}/amendments/vorverwalter-check`,
            { params: { category: cat, effective_date: form.effective_date } },
          );
          if (check?.count > 0) {
            const ok = await confirm({
              title: "Vor-Verwalter-Ausgleich entfernen?",
              description: `Es existieren ${check.count} automatisch erzeugte Vor-Verwalter-Ausgleich-Buchung(en) für den Zeitraum ab ${form.effective_date}. Diese basieren auf den ALTEN Sollbeträgen und passen nicht mehr zu den neuen. Sollen sie entfernt werden?`,
              confirmLabel: "Ja, entfernen",
              cancelLabel: "Behalten",
              danger: true,
            });
            if (ok) {
              const r = await api.post(
                `/tenancies/${tenancyId}/amendments/${data.id}/cleanup-ausgleich`,
                null,
                { params: { category: cat } },
              );
              toast.success(`${r.data.deleted} Vor-Verwalter-Ausgleich-Buchung(en) entfernt.`);
            }
          }
        } catch { /* Prüfung optional — Fehler nicht blockierend */ }
      }
      setShowAdd(false);
      setForm({ amendment_type: "rent_change", effective_date: "", new_value: "", reason: "", auto_regenerate: true });
      load();
      // iter159h — Parent informieren, damit Ledger/Kontostand ebenfalls
      // ohne extra „Neu laden"-Klick aktualisiert werden.
      if (typeof onChanged === "function") onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    // iter159k — Zuerst Preview holen, dann Dialog mit Liste der Monate
    // anzeigen (User-Feedback: „nicht blind rückgängig machen").
    try {
      const { data } = await api.get(`/tenancies/${tenancyId}/amendments/${id}/undo-preview`);
      const amendment = items.find((a) => a.id === id);
      setUndoPreview({
        id,
        typeLabel: TYPE_META[amendment?.amendment_type]?.label || "Änderung",
        ...data,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmUndo = async () => {
    if (!undoPreview) return;
    setUndoLoading(true);
    try {
      const { data } = await api.delete(`/tenancies/${tenancyId}/amendments/${undoPreview.id}`);
      const deleted = data?.deleted || 0;
      const created = data?.created || 0;
      toast.success(`Änderung zurückgesetzt · ${deleted} Buchung(en) entfernt, ${created} wiederhergestellt.`);
      setUndoPreview(null);
      load();
      if (typeof onChanged === "function") onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUndoLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="tenancy-amendments">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Vertragsänderungen</div>
          <div className="text-xs text-slate-500">
            Mietanpassungen, Vorauszahlungs-Änderungen, Einheiten-Zugänge — jeweils mit Wirkungsdatum.
          </div>
        </div>
        <Button size="sm" onClick={() => setShowAdd((s) => !s)} data-testid="amendment-add-toggle">
          <Plus size={14} className="mr-1" /> Neue Änderung
        </Button>
      </div>

      {showAdd && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="amendment-add-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Art der Änderung</div>
              <Select value={form.amendment_type} onValueChange={(v) => setForm((f) => ({ ...f, amendment_type: v }))}>
                <SelectTrigger data-testid="amendment-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Wirkungsdatum</div>
              <Input type="date" value={form.effective_date}
                     onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                     data-testid="amendment-effective-date" />
            </div>
            {!["unit_added", "unit_removed", "other"].includes(form.amendment_type) && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Neuer Wert (€)</div>
                <Input type="number" step="0.01" min="0" value={form.new_value}
                       onChange={(e) => setForm((f) => ({ ...f, new_value: e.target.value }))}
                       placeholder="z. B. 550" data-testid="amendment-new-value" />
              </div>
            )}
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Grund (optional)
              </div>
              <Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                     placeholder="z. B. Indexanpassung §557b BGB · BK-Neuberechnung 2025"
                     data-testid="amendment-reason" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={form.auto_regenerate}
                     onChange={(e) => setForm((f) => ({ ...f, auto_regenerate: e.target.checked }))}
                     data-testid="amendment-auto-regenerate" />
              Sollstellungen ab Wirkungsdatum automatisch neu berechnen
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Abbrechen</Button>
              <Button size="sm" onClick={create} data-testid="amendment-save">Speichern</Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Lade Änderungen…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-300 rounded-lg">
          Noch keine Vertragsänderungen erfasst.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => {
            const meta = TYPE_META[a.amendment_type] || TYPE_META.other;
            const Icon = meta.icon;
            return (
              <div key={a.id}
                   className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-shadow"
                   data-testid={`amendment-row-${a.id}`}>
                <div className={`p-2 rounded-md ${meta.color}`}><Icon size={16} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
                    <Badge variant="secondary" className="text-[10px] font-mono">
                      ab {fmtDate(a.effective_date)}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {a.previous_value !== null && a.previous_value !== undefined && (
                      <span className="tabular-nums">{eur(a.previous_value)} → </span>
                    )}
                    {a.new_value !== null && a.new_value !== undefined && (
                      <span className="tabular-nums font-semibold text-slate-900">{eur(a.new_value)}</span>
                    )}
                    {a.reason && <span className="ml-2 text-slate-500">· {a.reason}</span>}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => del(a.id)}
                        className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                        title="Änderung rückgängig machen"
                        data-testid={`amendment-delete-${a.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* 2026-02 — Verknüpfte Dokumente (Kategorie „Vertragsänderung") */}
      {uploadedDocs.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
            Anlagen ({uploadedDocs.length})
          </div>
          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-2.5 py-1.5">Titel</th>
                  <th className="text-left px-2.5 py-1.5">Datei</th>
                  <th className="text-left px-2.5 py-1.5">Hochgeladen am</th>
                  <th className="text-right px-2.5 py-1.5 pr-3">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {uploadedDocs.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100" data-testid={`amendment-doc-${d.id}`}>
                    <td className="px-2.5 py-1.5 font-medium text-slate-800">{d.title || d.original_filename}</td>
                    <td className="px-2.5 py-1.5 text-slate-600 truncate max-w-[220px]">{d.original_filename || "—"}</td>
                    <td className="px-2.5 py-1.5 tabular-nums text-slate-600">{fmtDate(d.created_at)}</td>
                    <td className="px-2.5 py-1.5 pr-3 text-right">
                      <Button variant="ghost" size="icon" className="h-6 w-6"
                              onClick={() => window.open(`/api/documents/${d.id}/download`, "_blank")}
                              title="Herunterladen"><Download size={12} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-500 italic mt-1">
            Tipp: Um ein Dokument hier anzuzeigen, im Dokumenten-Reiter beim Upload die Kategorie „Vertragsänderung" wählen.
          </div>
        </div>
      )}

      {/* iter159k — Undo-Preview Dialog */}
      <Dialog open={!!undoPreview} onOpenChange={(o) => !o && !undoLoading && setUndoPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="amendment-undo-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
                <Warning size={20} className="text-rose-600" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle>Änderung zurücksetzen?</DialogTitle>
                <DialogDescription className="mt-1.5">
                  {undoPreview?.typeLabel} ab {fmtDate(undoPreview?.effective_date)} —
                  die folgenden {undoPreview?.count || 0} Sollstellung(en) werden entfernt und
                  aus der wiederhergestellten Regel neu erzeugt.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {undoPreview?.months?.length > 0 ? (
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Monat</th>
                    <th className="text-right px-2 py-1.5">Aktuell (neu)</th>
                    <th className="text-right px-2 py-1.5">Wird zu (alt)</th>
                  </tr>
                </thead>
                <tbody>
                  {undoPreview.months.map((m) => (
                    <tr key={m.posting_date} className="border-t border-slate-100">
                      <td className="px-2 py-1 tabular-nums">{fmtDate(m.posting_date)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-600">{eur(m.current_amount)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold text-slate-900">
                        {m.restored_amount !== null && m.restored_amount !== undefined ? eur(m.restored_amount) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-slate-500 py-4 text-center border border-dashed rounded-md">
              Keine betroffenen Sollstellungen gefunden — nur die Regel wird zurückgesetzt.
            </div>
          )}
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Bereits vorhandene Ist-Buchungen (Zahlungen) bleiben erhalten und werden beim nächsten
            Bank-Abgleich neu zugeordnet.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUndoPreview(null)} disabled={undoLoading}
                    data-testid="amendment-undo-cancel">
              Abbrechen
            </Button>
            <Button onClick={confirmUndo} disabled={undoLoading}
                    className="bg-rose-600 hover:bg-rose-700"
                    data-testid="amendment-undo-confirm">
              {undoLoading ? "Wird zurückgesetzt…" : "Ja, zurücksetzen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
